import { sanitizeContent } from "@/lib/typography";
import ogSocialAsset from "@/assets/og-sylwia-sitkowska.jpg";
import { contentDate } from "@/content/content-dates";

export const SITE_URL = "https://sylwiasitkowska.pl";

/** Zamienia ścieżkę względną na pełny adres absolutny (wymagany przez OG i Schema.org). */
export function abs(path: string): string {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** "17.09.2025" -> "2025-09-17" (ISO 8601). Zwraca undefined dla braku/nieznanego formatu. */
export function isoDate(date?: string): string | undefined {
  if (!date) return undefined;
  const match = date.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return undefined;
  const [, d, m, y] = match;
  return `${y}-${m}-${d}`;
}

/** Domyślny obrazek Open Graph (pełny adres) — fallback dla schematów bez własnej grafiki. */
export const DEFAULT_OG_IMAGE = abs(ogSocialAsset);

export const PERSON_ID = `${SITE_URL}/#sylwia-sitkowska`;

export const personRef = { "@id": PERSON_ID } as const;

/** Jeden kanoniczny profil autorki, współdzielony przez stronę główną, oferty i artykuły. */
export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Sylwia Sitkowska",
  url: SITE_URL,
  image: DEFAULT_OG_IMAGE,
  sameAs: [
    "https://pl.linkedin.com/in/sylwia-sitkowska",
    "https://www.instagram.com/sitkowska.psycholog/",
    "https://www.youtube.com/@sylwiasitkowska",
    "https://www.przystan-psychologiczna.pl/",
  ],
  jobTitle: "Psycholog dla liderów, psychoterapeutka, przedsiębiorczyni",
  description:
    "Psycholog dla liderów i psychoterapeutka CBT z 10 000 h pracy 1:1 oraz właścicielka organizacji obejmującej 30 placówek i około 300 osób. Wspiera liderów, członków zarządów i właścicieli firm w sytuacjach wysokiej presji.",
  knowsAbout: [
    "psychologia biznesu",
    "wypalenie zawodowe liderów",
    "przywództwo",
    "zarządzanie stresem",
    "podejmowanie decyzji pod presją",
    "psychoterapia CBT",
    "relacje w zespole",
    "delegowanie i granice",
  ],
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Psychologia" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Psychoterapia CBT" },
  ],
  hasOccupation: {
    "@type": "Occupation",
    name: "Psycholog dla liderów i psychoterapeutka",
    occupationLocation: { "@type": "Country", name: "Polska" },
    skills: "psychologia biznesu, psychoterapia, rozwój liderów, zarządzanie stresem",
  },
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "Uniwersytet SWPS" },
    { "@type": "CollegeOrUniversity", name: "Politechnika Warszawska" },
  ],
  worksFor: {
    "@type": "Organization",
    "@id": "https://www.przystan-psychologiczna.pl/#organization",
    name: "Przystań Psychologiczna",
    url: "https://www.przystan-psychologiczna.pl/",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Kontakt biznesowy",
    availableLanguage: ["pl"],
    url: `${SITE_URL}/kontakt`,
  },
};

import type { MediaItem, EventItem } from "./site-content";

import { mediaThumbnail, isVideo, spotifyEpisodeId } from "@/lib/media";

const youtubeThumb = (url: string) => mediaThumbnail(url);

/** Article / VideoObject / PodcastEpisode dla pojedynczej pozycji medialnej. */
export function mediaItemSchema(item: MediaItem) {
  if (!item.href) return null;

  const published = isoDate(item.date);
  const base: Record<string, unknown> = {
    name: item.topic,
    headline: item.topic,
    url: item.href,
    inLanguage: "pl-PL",
    author: { "@type": "Person", name: "Sylwia Sitkowska", "@id": PERSON_ID },
    about: { "@type": "Person", name: "Sylwia Sitkowska", "@id": PERSON_ID },
    publisher: { "@type": "Organization", name: item.name },
  };
  if (published) base["datePublished"] = published;

  const thumb = youtubeThumb(item.href) ?? (item.src ? abs(item.src) : undefined);

  if (item.icon === "youtube" || isVideo(item.href)) {
    return {
      "@type": "VideoObject",
      ...base,
      description: item.topic,
      uploadDate: published ?? "2024-01-01",
      contentUrl: item.href,
      embedUrl: item.href,
      ...(thumb ? { thumbnailUrl: thumb } : {}),
    };
  }
  if (item.icon === "podcast" || spotifyEpisodeId(item.href)) {
    return {
      "@type": "PodcastEpisode",
      ...base,
      description: item.topic,
      associatedMedia: { "@type": "MediaObject", contentUrl: item.href },
    };
  }
  return {
    "@type": "Article",
    ...base,
    description: item.topic,
    image: thumb ?? DEFAULT_OG_IMAGE,
    mainEntityOfPage: { "@type": "WebPage", "@id": item.href },
  };
}

/** Event dla pojedynczego wystąpienia. */
export function eventSchema(event: EventItem) {
  return {
    "@type": "Event",
    name: event.title,
    description: event.topic,
    url: event.href ?? `${SITE_URL}/wystapienia#${event.id}`,
    image: event.logo ? abs(event.logo) : DEFAULT_OG_IMAGE,
    startDate: event.startDate,
    endDate: event.endDate ?? event.startDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: event.venue ?? event.place,
      address: {
        "@type": "PostalAddress",
        ...(event.streetAddress ? { streetAddress: event.streetAddress } : {}),
        addressLocality: event.city,
        addressCountry: "PL",
      },
    },
    performer: { "@type": "Person", name: "Sylwia Sitkowska", "@id": PERSON_ID },
    // Organizatora celowo nie oznaczamy: nie mamy potwierdzonych nazw
    // organizatorów poszczególnych wydarzeń, a tytuł wydarzenia nie jest
    // organizacją.
    ...(event.ticketsUrl
      ? {
          offers: {
            "@type": "Offer",
            url: event.ticketsUrl,
            availability: "https://schema.org/InStock",
          },
        }
      : {}),
  };
}

/** BreadcrumbList dla podstrony. */
export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: abs(item.path),
    })),
  };
}

/** Service + Offer dla programu (konsultacje / mastermind). */
export function serviceSchema(params: {
  name: string;
  description: string;
  path: string;
  price: string;
  audience: string;
  serviceType: string;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: params.name,
    description: params.description,
    url: abs(params.path),
    serviceType: params.serviceType,
    inLanguage: "pl-PL",
    provider: {
      "@type": "Person",
      "@id": PERSON_ID,
      name: "Sylwia Sitkowska",
      url: SITE_URL,
    },
    areaServed: params.areaServed ?? "PL",
    audience: { "@type": "BusinessAudience", name: params.audience },
    offers: {
      "@type": "Offer",
      price: params.price,
      priceCurrency: "PLN",
      availability: "https://schema.org/InStock",
      url: abs(params.path),
      category: "Usługa zwolniona z VAT",
    },
  };
}

/** WebSite dla strony głównej. */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "Sylwia Sitkowska",
  inLanguage: "pl-PL",
  publisher: { "@id": PERSON_ID },
};

import type { Article } from "./articles";

/** Article + FAQPage + BreadcrumbList + meta dla artykułu z bazy wiedzy. */
export function articleHead(article: Article, ogImage: string) {
  const url = `${SITE_URL}/${article.slug}`;
  const modified = contentDate(`/${article.slug}`, article.datePublished);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.h1,
    description: article.metaDescription,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "pl-PL",
    datePublished: article.datePublished,
    dateModified: modified,
    image: ogImage,
    keywords: article.keywords.join(", "),
    about: { "@type": "Thing", name: article.keyword },
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    citation: article.sources.map((source) => ({
      "@type": "CreativeWork",
      name: source.title,
      author: source.authors,
      datePublished: String(source.year),
      publisher: { "@type": "Organization", name: source.where },
      url: source.url,
    })),
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Strona główna", path: "/" },
    { name: "Psychologia biznesu", path: "/psychologia-biznesu" },
    { name: article.h1, path: `/${article.slug}` },
  ]);

  return sanitizeContent({
    meta: [
      { title: article.metaTitle },
      { name: "description", content: article.metaDescription },
      { name: "keywords", content: article.keywords.join(", ") },
      { property: "og:title", content: article.metaTitle },
      { property: "og:description", content: article.metaDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:image", content: ogImage },
      { property: "article:published_time", content: article.datePublished },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: article.metaTitle },
      { name: "twitter:description", content: article.metaDescription },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd) },
      ...(article.faq.length ? [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd) }] : []),
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  });
}

/** SpeakableSpecification: wskazuje AI, które fragmenty najlepiej cytować. */
export function speakableSchema(cssSelectors: string[]) {
  return {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  };
}

/** Article dla stron tematycznych (pillar pages) z dateModified, author, speakable. */
export function articlePageSchema(params: {
  title: string;
  description: string;
  path: string;
  aboutName: string;
  speakableSelectors: string[];
  dateModified?: string;
  image?: string;
}) {
  const url = abs(params.path);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: params.title,
    description: params.description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "pl-PL",
    image: params.image ?? DEFAULT_OG_IMAGE,
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    about: { "@type": "Thing", name: params.aboutName },
    ...(() => {
      const modified = params.dateModified ?? contentDate(params.path);
      return modified ? { dateModified: modified } : {};
    })(),
    speakable: speakableSchema(params.speakableSelectors),
  };
}

/** WebPage ze speakable dla stron informacyjnych bez formy artykułu. */
export function webPageSchema(params: {
  title: string;
  description: string;
  path: string;
  speakableSelectors: string[];
  aboutName?: string;
}) {
  const url = abs(params.path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: params.title,
    description: params.description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "pl-PL",
    ...(params.aboutName ? { about: { "@type": "Thing", name: params.aboutName } } : {}),
    speakable: speakableSchema(params.speakableSelectors),
  };
}

/** Rozbudowany Book dla pojedynczej publikacji (z ofertą księgarni). */
export function bookSchema(book: { title: string; src: string; tag?: string; url?: string }) {
  return {
    "@type": "Book",
    "@id": `${SITE_URL}/media-i-publikacje#${slugify(book.title)}`,
    name: book.title,
    headline: book.title,
    bookFormat: "https://schema.org/Paperback",
    image: abs(book.src),
    inLanguage: "pl",
    ...(book.tag ? { genre: book.tag, about: { "@type": "Thing", name: book.tag } } : {}),
    author: { "@type": "Person", name: "Sylwia Sitkowska", "@id": PERSON_ID },
    ...(book.url
      ? {
          url: book.url,
          workExample: {
            "@type": "Book",
            bookFormat: "https://schema.org/Paperback",
            inLanguage: "pl",
            potentialAction: { "@type": "ReadAction", target: book.url },
            offers: {
              "@type": "Offer",
              availability: "https://schema.org/InStock",
              url: book.url,
              seller: { "@type": "Organization", name: "Empik" },
            },
          },
        }
      : {}),
  };
}

/** ItemList publikacji książkowych. */
export function booksListSchema(
  items: { title: string; src: string; tag?: string; url?: string }[],
) {
  return {
    "@type": "ItemList",
    name: "Publikacje książkowe Sylwii Sitkowskiej",
    numberOfItems: items.length,
    itemListElement: items.map((book, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: book.url ?? `${SITE_URL}/media-i-publikacje`,
      item: bookSchema(book),
    })),
  };
}

/** ItemList wypowiedzi medialnych (Article / PodcastEpisode / VideoObject). */
export function mediaListSchema(items: MediaItem[], name = "Wypowiedzi medialne") {
  const entries = items.filter((item) => item.href);
  return {
    "@type": "ItemList",
    name,
    numberOfItems: entries.length,
    itemListElement: entries.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: item.href,
      item: mediaItemSchema(item),
    })),
  };
}

export function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
